import React from 'react';
import { Link } from 'react-router-dom';
import { STATE_STYLES, ACCOUNT_STYLES, TICKET_STATUS_STYLES } from './api';

export const Card = ({ children, className = '', accent, ...rest }) => (
  <div className={`portal-card rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 ${className}`} {...rest}>
    {accent && <span className={`portal-stat-accent ${accent}`} />}
    {children}
  </div>
);

export const CardTitle = ({ icon: Icon, children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
      {Icon && <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#C8102E] to-[#1E3A8A] text-white flex items-center justify-center shadow-md shadow-blue-900/20"><Icon className="h-3.5 w-3.5" /></span>}{children}
    </h3>
    {action}
  </div>
);

export const Skeleton = ({ className = '' }) => <div className={`animate-pulse rounded-lg bg-slate-200/80 dark:bg-slate-800 ${className}`} />;

export const StatusDot = ({ state, size = 'h-3 w-3' }) => {
  const s = STATE_STYLES[state] || STATE_STYLES.unknown;
  return (
    <span className={`relative inline-flex ${size}`}>
      {s.pulse && <span className={`absolute inset-0 rounded-full ${s.color} opacity-60 animate-ping`} />}
      <span className={`relative inline-flex rounded-full ${size} ${s.color}`} />
    </span>
  );
};

export const StateBadge = ({ connection }) => {
  const s = STATE_STYLES[connection?.state] || STATE_STYLES.unknown;
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${s.soft} ${s.text}`} data-testid="connection-state-badge">
      <StatusDot state={connection?.state} size="h-2.5 w-2.5" />{connection?.label || 'Unknown'}
    </span>
  );
};

export const AccountBadge = ({ status }) => (
  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${ACCOUNT_STYLES[status] || ACCOUNT_STYLES.Inactive}`} data-testid="account-status-badge">{status}</span>
);

export const TicketBadge = ({ status }) => (
  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${TICKET_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
);

export const PriorityBadge = ({ priority }) => {
  const m = { Low: 'text-slate-600 bg-slate-100', Medium: 'text-blue-700 bg-blue-50', High: 'text-amber-700 bg-amber-50', Critical: 'text-red-700 bg-red-50' };
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${m[priority] || m.Medium}`}>{priority}</span>;
};

export const Unavailable = ({ title = 'Data temporarily unavailable', reason, icon: Icon }) => (
  <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center bg-slate-50/60 dark:bg-slate-800/40" data-testid="data-unavailable">
    {Icon && <Icon className="h-8 w-8 mx-auto text-slate-400 mb-2" />}
    <p className="font-semibold text-slate-700 dark:text-slate-200">{title}</p>
    {reason && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">{reason}</p>}
  </div>
);

export const Field = ({ label, value, mono = false, testId }) => (
  <div className="py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-0 flex justify-between gap-4">
    <dt className="text-sm text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className={`text-sm font-semibold text-slate-800 dark:text-slate-100 text-right ${mono ? 'font-mono' : ''}`} data-testid={testId}>{value ?? '—'}</dd>
  </div>
);

export const QuickAction = ({ to, icon: Icon, label, onClick, testId }) => {
  const cls = 'portal-card group flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-[#C8102E] text-center';
  const inner = (<><span className="h-10 w-10 rounded-full bg-gradient-to-br from-[#C8102E] to-[#1E3A8A] text-white flex items-center justify-center group-hover:scale-110 transition-transform"><Icon className="h-5 w-5" /></span><span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{label}</span></>);
  return to ? <Link to={to} className={cls} data-testid={testId}>{inner}</Link> : <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
};

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const ErrorBox = ({ message, testId = 'error-message' }) => message ? (
  <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-sm px-3 py-2" role="alert" data-testid={testId}>{message}</div>
) : null;
